// Coach's tips — curated content authored statically. When AI ships, these become dynamic
// and personalized. Until then, this gives every workout day a real coaching voice.
//
// Structure: keyed by program-template key (matches templates in data/programs.js).
// Each entry returns one or more tips; the UI rotates them across visits using a deterministic
// day-based hash so the same user sees a stable tip per day.

export const tipsByTemplate = {
  // PPL
  push: [
    { focus: 'Tempo', body: 'Slow the eccentric (the lowering phase) on your first compound. 3 seconds down builds more muscle than yanking the bar.' },
    { focus: 'Cue', body: 'On every pressing rep, drive your shoulder blades down and back into the bench. Stable shoulders = safer joints and bigger numbers.' },
    { focus: 'Order', body: 'Triceps are smaller and fatigue faster — save isolation work for the end. Heavy compounds first, every time.' },
    { focus: 'Mindset', body: "Press day is about expressing force, not surviving it. If a set feels grindy from rep 1, the weight's too heavy." },
    { focus: 'Warm-up', body: 'Ramp into your first press — 1-2 lighter sets before working weight keeps the shoulder safe under the heaviest loads of the day.' },
  ],
  pull: [
    { focus: 'Cue', body: 'Pull with your elbows, not your hands. Imagine your hands are hooks — drive the elbows toward your hips or behind you.' },
    { focus: 'Tempo', body: 'On pulldowns and rows, pause for a full second when the bar touches you. The squeeze is where the growth lives.' },
    { focus: 'Form', body: 'Keep your chest up, not your chin. Lifting the chin shortens the cervical spine; lifting the chest opens the lats.' },
    { focus: 'Mindset', body: 'Back day is the day most people half-rep. Slow it down. Feel it. The biceps should be the support cast, not the star.' },
    { focus: 'Warm-up', body: 'Warm the grip and shoulders before your first pull — 1-2 lighter sets protect the elbows on heavy rows and pulldowns.' },
  ],
  legs: [
    { focus: 'Cue', body: 'On every squat variation: spread the floor with your feet. This activates glute-med and stabilizes the knee.' },
    { focus: 'Depth', body: "If you can't hit parallel without rounding your lower back, your hips are tight or the weight is wrong. Both are fixable today." },
    { focus: 'Tempo', body: 'Pause at the bottom of one set per exercise. A two-second pause kills momentum and forces real strength.' },
    { focus: 'Mindset', body: "Leg day is where character is built. Most people skip it. You're not most people." },
    { focus: 'Warm-up', body: 'Hips and knees need a proper ramp — ease into your first squat or hinge with 1-2 lighter sets before working weight.' },
  ],
  core: [
    { focus: 'Quality', body: 'Core work is anti-movement, not movement. Anti-extension (planks), anti-rotation (Pallof), anti-lateral (suitcase carry). Quality > quantity.' },
    { focus: 'Breathing', body: 'Breathe behind the brace — air in through the nose to fill the belly, exhale forcefully without releasing tension.' },
    { focus: 'Frequency', body: 'Core trains best with daily exposure. 5-10 quality minutes wins over 45 sloppy minutes once a week.' },
  ],

  // Upper / Lower
  upper: [
    { focus: 'Balance', body: 'One push for every pull. If you bench heavy, you owe your shoulders a row or face pull at the same intensity.' },
    { focus: 'Cue', body: 'Set your back before every set. Shoulders down, ribs over hips, glutes lightly engaged. This is your foundation.' },
    { focus: 'Mindset', body: 'Upper days are about quality reps, not survival. Pick weights where rep 1 looks like rep 10.' },
    { focus: 'Warm-up', body: 'Ease into your first press or pull — 1-2 lighter sets building to working weight protects the shoulders and elbows before you load up.' },
  ],
  lower: [
    { focus: 'Warm-up', body: 'Hip flexors and ankles open or close everything below the belt. Spend 5 minutes warming both before the first heavy set.' },
    { focus: 'Tempo', body: 'On split squats and lunges, lower for 3 seconds. Your knees and glutes will thank you in 10 years.' },
    { focus: 'Recovery', body: 'After a heavy lower day, walk for 15 minutes. Light movement clears metabolites and cuts soreness in half.' },
  ],

  // Full Body
  'full-a': [
    { focus: 'Order', body: 'Compounds first, isolation last. Save energy for the lifts that move the most weight and recruit the most muscle.' },
    { focus: 'Mindset', body: 'Full Body Day A is your strength day. Push intensity on the squat and press — let the rest be in support.' },
    { focus: 'Warm-up', body: 'Full Body loads the biggest joints first — ease into your opening squat with 1-2 lighter ramp sets before working weight.' },
  ],
  'full-b': [
    { focus: 'Order', body: 'Day B emphasizes posterior chain. Your hinge and pull patterns set the tone — keep them crisp.' },
    { focus: 'Recovery', body: 'Full body 3x/week works because of recovery between sessions. Sleep is non-negotiable on these programs.' },
    { focus: 'Warm-up', body: 'A cold hinge is a risky hinge — ease into your first squat or deadlift variation with 1-2 lighter sets before working weight.' },
  ],
  full: [
    { focus: 'Focus', body: "Full body workouts ask for focus on the lifts that matter most. Don't skip the warm-up — your nervous system needs it." },
    { focus: 'Mindset', body: 'Showing up is 80%. The other 20% is honest effort on every set.' },
  ],

  // Bro Split
  chest: [
    { focus: 'Angle', body: 'Flat → Incline → Decline → Fly. Hitting every angle in one session means every fiber gets a touch.' },
    { focus: 'Cue', body: 'On flyes, keep a soft elbow bend the entire rep. Straight arms = elbow injury. Locked-out angles = stagnant pecs.' },
    { focus: 'Tempo', body: 'Pause at the bottom of one pressing variation. The stretched position is where chest growth happens.' },
    { focus: 'Warm-up', body: 'Chest day starts heavy — take your first pressing variation through 1-2 lighter ramp sets before your top set.' },
  ],
  back: [
    { focus: 'Variety', body: 'Vertical AND horizontal pulls in the same session. Pull-ups + rows builds a complete back; either alone leaves gaps.' },
    { focus: 'Cue', body: 'Mind your scapulae. Initiate every pull by retracting the shoulder blades, finish by depressing them.' },
    { focus: 'Volume', body: 'The back can handle more total volume than most muscle groups. 18-24 working sets across the week is the sweet spot.' },
    { focus: 'Warm-up', body: 'Grip and shoulders first — 1-2 lighter pulls before your heaviest set keeps the elbows and low back safe.' },
  ],
  shoulders: [
    { focus: 'Health', body: 'For every overhead press, do 2-3 sets of rear-delt or external rotation work. Healthy shoulders out-press injured ones.' },
    { focus: 'Cue', body: 'On lateral raises, lead with your elbow, not your wrist. Pinky up cue keeps the medial delt loaded.' },
    { focus: 'Tempo', body: 'Light weight + slow tempo > heavy weight + sloppy form on shoulders. Always.' },
    { focus: 'Warm-up', body: 'Shoulders are the most injury-prone joint in the gym — always ramp your first press with 1-2 lighter sets.' },
  ],
  arms: [
    { focus: 'Cue', body: 'Pin the elbows. On curls and pushdowns, elbows shouldn\'t move forward or back — only the forearm rotates.' },
    { focus: 'Stretch', body: 'Long-head triceps grow under stretch. Overhead extensions hit what pushdowns miss.' },
    { focus: 'Mindset', body: 'Arms are about mind-muscle connection more than load. Feel every contraction.' },
    { focus: 'Warm-up', body: 'Elbows take a beating on arm day — ease into your first curl or pushdown with a lighter set before loading up.' },
  ],
  quads: [
    { focus: 'Tempo', body: 'Slow the eccentric. 3 seconds down on every squat variation triples the time under tension.' },
    { focus: 'Foot position', body: 'Heels-elevated squats put more emphasis on the quads. Olympic shoes or a small plate works.' },
    { focus: 'Warm-up', body: 'Knees need the ramp more than any other joint — ease into your first squat with 1-2 lighter sets.' },
  ],
  postchain: [
    { focus: 'Hinge', body: 'Push the hips back, not the chest down. Your spine should stay neutral from setup to lockout.' },
    { focus: 'Stretch', body: 'Find the stretch, then drive through it. RDLs done right look like a slow controlled fall, not a quick drop.' },
    { focus: 'Warm-up', body: 'Hinge patterns punish a cold lower back — ramp your first RDL or deadlift variation before working weight.' },
  ],
  'arms-core': [
    { focus: 'Pacing', body: 'Superset arms with core moves to compress the session — bicep curl into hanging knee raise, pushdown into plank.' },
    { focus: 'Quality', body: 'Both arms and core respond to slow controlled reps. Save the speed work for another day.' },
    { focus: 'Warm-up', body: 'A lighter first set on your opening move wakes up the elbows before you push the pace.' },
  ],

  // Calisthenics
  calisthenics: [
    { focus: 'Progression', body: "No gym means progression is in tempo, range, and leverage. Slow it down, deepen the range, harder variation." },
    { focus: 'Mindset', body: "Bodyweight is never 'just bodyweight.' A slow controlled push-up beats a sloppy bench every time." },
    { focus: 'Warm-up', body: 'Bodyweight still needs a ramp — a slower, easier first set of your opening move preps the joints for full range.' },
  ],

  // Fallback
  rest: [
    { focus: 'Recovery', body: "Rest days are growth days. Sleep, hydrate, eat protein. The work is already in your muscles — don't undo it." },
  ],
  recovery: [
    { focus: 'Intent', body: 'Recovery doesn\'t mean lazy. Move at conversation pace — heart rate up enough to flush, low enough to talk.' },
  ],
};

// Pick today's tip deterministically — same user sees the same tip per (date, template) pair.
const hashString = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

export const getTodaysTip = (templateKey, dateIso) => {
  const list = tipsByTemplate[templateKey] || tipsByTemplate.full || [];
  if (list.length === 0) return null;
  const key = `${templateKey}-${dateIso || new Date().toDateString()}`;
  return list[hashString(key) % list.length];
};

// ----- Daily Motivator content (used on Dashboard widget) -----

export const motivators = {
  quotes: [
    { text: 'Iron sharpens iron, and one man sharpens another.', source: 'Proverbs 27:17' },
    { text: 'The pain you feel today is the strength you feel tomorrow.', source: 'Arnold Schwarzenegger' },
    { text: 'Discipline is choosing between what you want now and what you want most.', source: 'Abraham Lincoln' },
    { text: 'The body achieves what the mind believes.', source: 'Napoleon Hill' },
    { text: 'It never gets easier. You just get stronger.', source: 'Jordan Hoechlin' },
    { text: 'You are confined only by the walls you build yourself.', source: 'Andrew Murphy' },
    { text: 'The successful warrior is the average man with laser-like focus.', source: 'Bruce Lee' },
    { text: 'Don\'t count the days. Make the days count.', source: 'Muhammad Ali' },
    { text: 'Strength does not come from physical capacity. It comes from an indomitable will.', source: 'Mahatma Gandhi' },
    { text: 'The fight is won or lost far away from witnesses — behind the lines, in the gym, and out there on the road.', source: 'Muhammad Ali' },
    { text: 'Hardships often prepare ordinary people for an extraordinary destiny.', source: 'C.S. Lewis' },
    { text: 'Comfort is the enemy of progress.', source: 'P.T. Barnum' },
  ],
  cues: [
    { tag: 'Form check', body: 'Before your first heavy set, ask: Could I take a picture of this position and be proud?' },
    { tag: 'Breath', body: 'Inhale into the belly before the lift. Brace as you descend. Exhale through the sticking point.' },
    { tag: 'Tempo', body: 'Slowing down is the single fastest way to add muscle. 3 seconds eccentric. Try it for one set today.' },
    { tag: 'Mindset', body: "If today's session feels like a chore, change one thing — music, tempo, weight, exercise order. Carve new neural paths." },
    { tag: 'Recovery', body: 'You don\'t grow during the workout. You grow during the meal and the sleep that follow. Both matter.' },
    { tag: 'Hydration', body: 'Down 16 oz of water 30 min before training. Dehydration costs you 10-20% of your strength.' },
  ],
  mission: [
    { tag: 'IronLimb', body: 'A portion of every Enferno subscription funds IronLimb Foundation grants for amputee athletes. Your work fuels theirs.' },
    { tag: 'Built for you', body: 'Enferno was built for the multi-discipline, mid-life, pain-aware, limitation-respecting athletes who refuse to quit. That\'s you.' },
    { tag: 'You\'re early', body: 'Private beta. v0.3. Your reactions shape v1.0. Tap the Feedback button anytime.' },
  ],
};

export const getDailyMotivator = (dateIso) => {
  const types = ['quotes', 'cues', 'mission'];
  const dateKey = dateIso || new Date().toDateString();
  const typeIdx = hashString(dateKey) % types.length;
  const type = types[typeIdx];
  const list = motivators[type];
  const item = list[hashString(`${dateKey}-${type}`) % list.length];
  return { type, item };
};
